import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Marker, useMapEvents, LayersControl, useMap } from 'react-leaflet'
import L from 'leaflet'
import area from "@turf/area";
import { polygon } from "@turf/helpers";
import 'leaflet/dist/leaflet.css'

// ─── Area computation (spherical excess, metres²) ─────────────────────────────
const { BaseLayer } = LayersControl;

function computeAreaM2(points) {
  if (points.length < 3) return 0;
  const coordinates = [[
    ...points.map(p => [p.lng, p.lat]),
    [points[0].lng, points[0].lat],
  ]];
  const poly = polygon(coordinates);
  return area(poly);
}

// Search
function MapViewUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 15, { animate: true });
    }
  }, [center, map]);
  return null;
}

// ─── Captures map clicks while drawing ────────────────────────────────────────
function DrawingLayer({ isDrawing, onAddPoint }) {
  useMapEvents({
    click(e) {
      if (isDrawing) onAddPoint([e.latlng.lat, e.latlng.lng])
    },
  })
  return null
}


const btnBase = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontWeight: 600,
  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  pointerEvents: 'auto',
  whiteSpace: 'nowrap',
}

// ─── Map component ───────────────────────────
function MapComponent({ mapCenter, setGeoFence }) {
  const [isDrawing,  setIsDrawing]  = useState(false)
  const [points,     setPoints]     = useState([])     
  const [completed,  setCompleted]  = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [activeCenter, setActiveCenter] = useState([mapCenter.lat, mapCenter.lng])
  const [searchedMarker, setSearchedMarker] = useState(null)
  
  const [heading, setHeading] = useState(0);
  const [hasCompassData, setHasCompassData] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const handleOrientation = useCallback((e) => {
    let compassHeading = null;
    
    if (e.webkitCompassHeading !== undefined && e.webkitCompassHeading !== null) {
      compassHeading = e.webkitCompassHeading;
    } 
    else if (e.absolute === true && typeof e.alpha === 'number') {
      compassHeading = 360 - e.alpha; 
    } 

    if (compassHeading !== null) {
      setHeading(compassHeading);
      setHasCompassData(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("deviceorientationabsolute", handleOrientation);
    window.addEventListener("deviceorientation", handleOrientation);

    return () => {
      window.removeEventListener("deviceorientationabsolute", handleOrientation);
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, [handleOrientation]);

  const requestCompassPermission = async () => {
    try {
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        const permissionState = await DeviceOrientationEvent.requestPermission();
        if (permissionState !== 'granted') {
          setPermissionDenied(true);
          console.warn('Compass permission denied.');
        }
      }
    } catch (error) {
      console.error('Error requesting compass permission:', error);
    }
  };

  const handleDrawClick = useCallback(() => setIsDrawing(true), [])
  const handleCancelDraw = useCallback(() => setIsDrawing(false), [])

  const handleComplete = useCallback(() => {
    if (points.length < 3) {
      alert('Draw at least 3 points to create a polygon.')
      return
    }
    setIsDrawing(false)
    setCompleted(true)

    const paths   = points.map(([lat, lng]) => ({ latitude: lat, longitude: lng }))
    const polyArea = computeAreaM2(points.map(([lat, lng]) => ({ lat, lng })))
    setGeoFence(paths, Number(polyArea.toFixed(2)));
  }, [points, setGeoFence])

  const handleReset = useCallback(() => {
    setPoints([])
    setIsDrawing(false)
    setCompleted(false)
    setGeoFence([], 0)
  }, [setGeoFence])

  const addPoint = useCallback((pt) => setPoints(prev => [...prev, pt]), [])

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const coordRegex = /^\s*(-?\d+(\.\d+)?)[,\s]+(-?\d+(\.\d+)?)\s*$/;
    const match = searchQuery.match(coordRegex);

    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[3]);
      
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        setActiveCenter([lat, lng]);
        setSearchedMarker([lat, lng]);
        return;
      }
    }

    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`);
      const data = await res.json();
      
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setActiveCenter([lat, lon]);
        setSearchedMarker([lat, lon]);
      } else {
        alert("Location not found. Try a different place name or enter valid lat, lng coordinates.");
      }
    } catch (err) {
      console.error(err);
      alert("Search failed. Please try again later.");
    }
  };

  const directionalIcon = useMemo(() => {
    const html = `
      <div style="transform: rotate(${heading}deg); transform-origin: center; transition: transform 0.15s ease-out; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; position: relative;">
        <svg viewBox="0 0 40 40" width="40" height="40" style="position: absolute;">
          <path d="M20,20 L10,5 Q20,-2 30,5 Z" fill="rgba(255, 107, 0, 0.35)" />
          <circle cx="20" cy="20" r="7" fill="#FF6B00" stroke="#ffffff" stroke-width="2" />
        </svg>
      </div>
    `;
    return L.divIcon({
      html,
      className: '',
      iconSize: [40, 40],
      iconAnchor: [20, 20], 
    });
  }, [heading]);

  const showCompassPrompt = 
    typeof DeviceOrientationEvent !== 'undefined' && 
    typeof DeviceOrientationEvent.requestPermission === 'function' && 
    !hasCompassData && 
    !permissionDenied;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {isDrawing && (
        <style>{`.leaflet-container { cursor: crosshair !important; }`}</style>
      )}

      {showCompassPrompt && (
        <div style={{
          position: 'absolute',
          top: 70,
          right: 15,
          zIndex: 1000,
          pointerEvents: 'auto'
        }}>
          <button 
            onClick={requestCompassPermission} 
            style={{ 
              ...btnBase, 
              background: 'rgba(255, 255, 255, 0.9)', 
              color: '#333', 
              fontSize: '13px', 
              padding: '6px 12px',
              border: '1px solid #ccc',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            Use compass direction <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>→</span>
          </button>
        </div>
      )}

      {/* Search Bar Overlay */}
      <div style={{
        position: 'absolute',
        top: 10,
        left: 50,       
        right: 60,      
        zIndex: 1000,
        pointerEvents: 'none', 
        display: 'flex',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '5px',
          borderRadius: '5px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: '400px'
        }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '6px', margin: 0 }}>
            <input
              type="text"
              placeholder="Search place or lat, lng..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ 
                flex: 1, 
                minWidth: 0, 
                padding: '6px 10px', 
                border: '1px solid #ccc', 
                borderRadius: '4px', 
                outline: 'none' 
              }}
            />
            <button 
              type="submit" 
              style={{ 
                ...btnBase, 
                background: '#212529', 
                color: '#fff', 
                padding: '6px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Search"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                fill="currentColor" 
                viewBox="0 0 16 16"
              >
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
              </svg>
            </button>
          </form>
        </div>
      </div>

      <MapContainer
        center={activeCenter}
        zoom={14}
        maxZoom={20}
        style={{ width: '100%', height: '550px' }}
        zoomControl
        attributionControl
      >
        <MapViewUpdater center={activeCenter} />

        <LayersControl position="topright">
          <BaseLayer checked name="Map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
              maxZoom={19}
            />
          </BaseLayer>

          <BaseLayer name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
              maxNativeZoom={18}
              maxZoom={22}
            />
          </BaseLayer>
        </LayersControl>

        <DrawingLayer isDrawing={isDrawing} onAddPoint={addPoint} />

        {mapCenter && mapCenter.lat && (
           <Marker
             position={[mapCenter.lat, mapCenter.lng]}
             icon={directionalIcon}
           />
        )}

        {searchedMarker && (
          <CircleMarker
            center={searchedMarker}
            radius={8}
            pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#dc3545', fillOpacity: 1 }}
          />
        )}

        {!completed && points.length > 0 && (
          <>
            <Polyline
              positions={points}
              pathOptions={{ color: '#113460', weight: 2, dashArray: '6,4' }}
            />
            {points.map((pt, i) => (
              <CircleMarker
                key={i}
                center={pt}
                radius={5}
                pathOptions={{ color: '#113460', fillColor: '#199ee0', fillOpacity: 1, weight: 1.5 }}
              />
            ))}
          </>
        )}

        {completed && points.length >= 3 && (
          <Polygon
            positions={points}
            pathOptions={{
              fillColor:    '#199ee0',
              fillOpacity:  0.2,
              color:        '#113460',
              weight:       2,
            }}
          />
        )}
      </MapContainer>

      {/* ── Control overlay ── */}
      <div
        style={{
          position: 'absolute',
          bottom: 20, 
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          display: 'flex',
          flexWrap: 'wrap', 
          justifyContent: 'center',
          gap: 8,
          width: '90%', 
          pointerEvents: 'none',
        }}
      >
        {/* Before drawing starts */}
        {!isDrawing && !completed && points.length === 0 && (
          <button onClick={handleDrawClick} style={{ ...btnBase, background: '#0d6efd', color: '#fff' }}>
            Draw Farm Boundary
          </button>
        )}

        {/* While drawing */}
        {isDrawing && (
          <>
            <button onClick={handleCancelDraw} style={{ ...btnBase, background: '#dc3545', color: '#fff' }}>
              Cancel Drawing
            </button>
            {points.length >= 3 && (
              <button onClick={handleComplete} style={{ ...btnBase, background: '#198754', color: '#fff' }}>
                Complete Polygon
              </button>
            )}
          </>
        )}

        {/* Points drawn but drawing cancelled */}
        {!isDrawing && !completed && points.length > 0 && (
          <>
            <button onClick={() => setIsDrawing(true)} style={{ ...btnBase, background: '#0d6efd', color: '#fff' }}>
              Continue Drawing
            </button>
            {points.length >= 3 && (
              <button onClick={handleComplete} style={{ ...btnBase, background: '#198754', color: '#fff' }}>
                Complete Polygon
              </button>
            )}
            <button onClick={handleReset} style={{ ...btnBase, background: '#ffc107', color: '#000' }}>
              Reset
            </button>
          </>
        )}

        {/* Polygon completed */}
        {completed && (
          <button onClick={handleReset} style={{ ...btnBase, background: '#ffc107', color: '#000' }}>
            Reset
          </button>
        )}
      </div>
    </div>
  )
}

export default React.memo(MapComponent)